import { Box, Center, Flex, Grid, Loader, Text } from '@mantine/core'
import { ProgramLayout } from '@ospace/layout'
import { ErrorBoundary } from '@ospace/shared'
import { useVendorSalesPerson } from '@ospace/shared/hooks/useVendorSalesPerson'
import { Link, useParams } from 'react-router-dom'

import { ContactsDiscoveryCard } from '../../components/ContactsDiscoveryCard'
import { MultiChannelWidget, TeleSalesWidget } from '../../components/outreach-widget'
import OpportunitiesTable from '../../components/partials/dashboard/OpportunitiesTable'
import { PipelineStatus } from '../../components/PipelineStatus'
import { RevenueForecast } from '../../components/RevenueForecast'
import {
  useCampaign,
  useDeals,
  useMCOstats,
  useOpportunityStats,
  usePipelineStatus,
} from '../../hooks'

type Props = { campaignId: number }

type MCOStatsData = {
  total_emails_sent: string
  hs_total_emails_sent: string
  emails_open_rate: string
  hs_open_rate_percentage: string
  total_linkedin_requests_sent: string
  total_linkedin_requests_accepted: string
}

export const NewOverviewPage = (props: Props) => {
  const { data: campaign } = useCampaign(props.campaignId)
  const deals = useDeals(props.campaignId)
  const { data: vendorSalesPerson, isFetching: vendorIsFetching } = useVendorSalesPerson(
    props.campaignId
  )
  const dealStats = useOpportunityStats(props.campaignId)
  const mcoStats = useMCOstats(props.campaignId)

  const { isLoading, data: pipelineStatusData } = usePipelineStatus(props.campaignId)
  const currency = campaign?.client?.currency || ''

  const mcoData = (mcoStats.data as MCOStatsData) || {}

  const totalEmailsSent = Number(mcoData.total_emails_sent) + Number(mcoData.hs_total_emails_sent)

  const openLemlist = Number(mcoData.emails_open_rate)
  const openHS = Number(mcoData.hs_open_rate_percentage)

  const divisor = openLemlist > 0 && openHS > 0 ? 2 : 1
  const openRate = (openLemlist + openHS) / divisor

  return (
    <ProgramLayout
      status={deals.isFetching || vendorIsFetching ? 'loading' : 'ready'}
      pageId='programs'
      fluid={true}
    >
      <Box mt='md'>
        <Flex gap='sm' align='stretch' pb='sm'>
          <Grid
            style={{
              width: '100%',
              margin: '0',
            }}
          >
            <Grid.Col span={5}>
              <Grid>
                <Grid.Col
                  span={6}
                  style={{
                    paddingLeft: 0,
                  }}
                >
                  <RevenueForecast
                    pipelineStatusData={pipelineStatusData}
                    isLoading={isLoading}
                    currency={currency}
                  />
                </Grid.Col>
                <Grid.Col span={6} h='100%'>
                  <PipelineStatus
                    pipelineStatusData={pipelineStatusData}
                    isLoading={isLoading}
                    currency={currency}
                  />
                </Grid.Col>
              </Grid>
            </Grid.Col>
            <Grid.Col
              span={5}
              style={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Flex
                gap='sm'
                align='stretch'
                style={{
                  flexBasis: '50%',
                }}
              >
                <div
                  style={{
                    flexBasis: '100%',
                    // paddingTop: 'sm',
                  }}
                >
                  {mcoStats.isFetching ? (
                    <Center h='100%' w='100%'>
                      <Loader />
                    </Center>
                  ) : (
                    <MultiChannelWidget
                      emailsSent={totalEmailsSent}
                      openRate={`${openRate.toFixed(2)} %`}
                      requests={Number(mcoData.total_linkedin_requests_sent)}
                      accepted={Number(mcoData.total_linkedin_requests_accepted)}
                    />
                  )}
                </div>
              </Flex>
              <Flex
                gap='sm'
                align='stretch'
                style={{
                  flexBasis: '50%',
                }}
              >
                <div
                  style={{
                    flexBasis: '100%',
                    paddingTop: '0.75rem',
                  }}
                >
                  {dealStats.isFetching ? (
                    <Center h='100%' w='100%'>
                      <Loader />
                    </Center>
                  ) : (
                    <TeleSalesWidget
                      htn={{
                        value: dealStats.data?.teleSalesOutreach['HTN'],
                        delta: dealStats.data?.telseSalesOutreachThisWeek['HTN']
                          ? `+ ${dealStats.data?.telseSalesOutreachThisWeek['HTN']} this week`
                          : ``,
                      }}
                      ltn={{
                        value: dealStats.data?.teleSalesOutreach['LTN'],
                        delta: dealStats.data?.telseSalesOutreachThisWeek['LTN']
                          ? `+ ${dealStats.data?.telseSalesOutreachThisWeek['LTN']} this week`
                          : ``,
                      }}
                      upcomingMeetings={{
                        value: dealStats.data?.teleSalesOutreach['Upcoming Meetings'],
                        delta: dealStats.data?.telseSalesOutreachThisWeek['Upcoming Meetings']
                          ? `+ ${dealStats.data?.telseSalesOutreachThisWeek['Upcoming Meetings']} this week`
                          : ``,
                      }}
                      requestedCallbacks={{
                        value: dealStats.data?.teleSalesOutreach['Call Back'],
                        delta: dealStats.data?.telseSalesOutreachThisWeek['Call Back']
                          ? `+ ${dealStats.data?.telseSalesOutreachThisWeek['Call Back']} this week`
                          : ``,
                      }}
                    />
                  )}
                </div>
              </Flex>
            </Grid.Col>
            <Grid.Col
              span={2}
              style={{
                display: 'flex',
                flexDirection: 'column',
                paddingRight: 0,
              }}
            >
              {dealStats.isFetching || mcoStats.isFetching ? (
                <Center h='100%' w='100%'>
                  <Loader />
                </Center>
              ) : (
                <ContactsDiscoveryCard
                  isFetching={mcoStats.isFetching || dealStats.isFetching}
                  companies={dealStats.data.discovery['companies']}
                  contacts={dealStats.data.discovery['contacts']}
                  rateOfChange={
                    dealStats.data.discoveryThisWeek['contacts'] !== '0'
                      ? `+ ${dealStats.data.discoveryThisWeek['contacts']}`
                      : ``
                  }
                />
              )}
            </Grid.Col>
          </Grid>
        </Flex>
        {deals.isFetching ? (
          <Loader />
        ) : deals?.data && deals.data.length > 0 ? (
          <OpportunitiesTable deals={deals} vendorSalesPerson={vendorSalesPerson} />
        ) : (
          <Text>No deals found</Text>
        )}

        {deals.data && deals.data.length === 0 && (
          <Text c='grey'>
            If you are looking for the previous Program Metrics page,{' '}
            <Link to={`/program/view/${campaign?.id}/program-metrics`}>click here</Link>
          </Text>
        )}
      </Box>
    </ProgramLayout>
  )
}

const Controller = () => {
  const params: any = useParams()
  const campaignId = Number(params?.id)

  return <NewOverviewPage campaignId={campaignId} />
}

export const NewOverviewPageRouteHandler = () => (
  <ErrorBoundary>
    <Controller />
  </ErrorBoundary>
)
